import { useState } from "react";
import CustomButton from "../CustomButton";
import ModalLayout from "../layouts/ModalLayout";
import admissionApis from "../../api/admissionApis";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import ConfirmationModal from "./ConfirmationModal";
import { fetchAdmissions } from "../../features/admission/admissionSlice";
import SelectClassModal from "./SelectClassModel";
import { useBranchCode } from "../../util/hooks/useBranch";

const statusList = [
  "ADMISSION_OFFERED",
  "ADMISSION_ON_HOLD",
  "ADMISSION_ACCEPTED",
  "ADMISSION_REJECTED",
] as const;

type StatusType = (typeof statusList)[number];

interface Props {
  admissionId: number;
  onClose?: () => void;
}

export default function AdmissionStatus({ admissionId, onClose }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { page, size, filters } = useSelector(
    (state: RootState) => state.admissions,
  );

  const [selected, setSelected] = useState<StatusType | "">("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const branchCode = useBranchCode();

  const refreshList = () => {
    dispatch(fetchAdmissions({ page, size, filters, branchCode }));
  };

  const handleSaveStatus = async (classId?: number) => {
    if (!selected || !admissionId) return;

    await admissionApis.moveAdmissionToClass({
      admissionId,
      status: selected,
      classId,
    });

    refreshList();
    onClose?.();
  };

  const handleSaveClick = () => {
    if (selected === "ADMISSION_OFFERED") {
      setShowConfirm(true);
    } else {
      handleSaveStatus();
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    setShowClassModal(true);
  };

  const handleClassConfirm = (classId: number) => {
    handleSaveStatus(classId);
  };

  return (
    <>
      <ModalLayout onClose={onClose} width="max-w-sm">
        <div className="flex flex-col gap-6">
          <h4 className="text-xl font-semibold text-neutral-400">
            Admission Status
          </h4>

          <div className="flex flex-col gap-3">
            {statusList.map((status, index) => (
              <label key={index} className="flex gap-3 cursor-pointer">
                <input
                  type="radio"
                  checked={selected === status}
                  onChange={() => setSelected(status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-3">
            <CustomButton
              text="Close"
              color="bg-neutral-200"
              onClick={onClose}
            />
            <CustomButton
              text="Save"
              color="bg-success-600"
              disabled={!selected}
              onClick={handleSaveClick}
            />
          </div>
        </div>
      </ModalLayout>

      {showConfirm && (
        <ConfirmationModal
          title="Status Change"
          message="Are you sure you want to mark this student as ADMISSION OFFERED?"
          confirmText="Yes"
          cancelText="No"
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}

      {showClassModal && (
        <SelectClassModal
          title="Select Class"
          actionLabel="Confirm"
          onClose={() => setShowClassModal(false)}
          onAction={(classIds) => {
            const classId = classIds[0];
            handleSaveStatus(classId);
          }}
          selectionMode="single"
        />
      )}
    </>
  );
}
